import { Seo } from "../components/Seo";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BookOpen, Palette, Heart, Users, CalendarDays, GraduationCap,
  Camera, Crown, Mail, Star, Sparkles, ArrowRight, Cloud, Award, Wand2,
} from "lucide-react";
import { IMAGES, SCENES, FRIENDS } from "../lib/content";
import { kingArt } from "../lib/kingart";
import { Btn, CurvedDivider } from "../components/kit";
import { Decorations, Butterfly } from "../components/Decorations";
import AnimatedCharacter from "../components/kingdom/AnimatedCharacter";
import WordOfWeekWidget from "../components/wow/WordOfWeekWidget";
import FeaturedShowcase from "../components/home/FeaturedShowcase";
import { SkyFeature, useSkyFeatured } from "../components/home/SkyAdventureFeature";

// New approved book-holding Sir Gingerman artwork (Welcome page only). The old
// /hero-gingerman.png is intentionally left in place for every other location and
// for rollback — revert this constant to IMAGES.hero to restore the previous art.
const HERO_BOOKS = "/hero-gingerman-books-v2.png";
const HERO_BOOKS_BLINK = "/hero-gingerman-books-v2-blink.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.55, ease: "easeOut" } }),
};

const bigButtons = [
  { to: "/books", label: "Read Books", Icon: BookOpen, bg: "bg-purpleK", note: "Story time!" },
  { to: "/activities", label: "Today's Activity", Icon: Palette, bg: "bg-orangeK", note: "Let's create!" },
  { to: "/feelings", label: "How Are You Feeling?", Icon: Heart, bg: "bg-tealK", note: "Check in" },
];

const previews = [
  { to: "/friends", label: "Friends' Neighborhood", Icon: Users, color: "#FF8C3B", desc: "Meet the Kingdom crew" },
  { to: "/coloring", label: "Coloring Studio", Icon: Palette, color: "#5BC0EB", desc: "Print & color the Kingdom" },
  { to: "/resources", label: "Grown-Up Helpers", Icon: GraduationCap, color: "#26C0B4", desc: "Guides & printables" },
  { to: "/blog", label: "Kingdom Gallery", Icon: Camera, color: "#FFC839", desc: "Stories, tips & art" },
];

export default function Home() {
  const heroRef = useRef(null);
  const [greet, setGreet] = useState(0);
  const [hovered, setHovered] = useState(false);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const charY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const cloudX = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const sky = useSkyFeatured();

  return (
    <div data-testid="home-page">
      <Seo path="/" />
      {/* ================= CINEMATIC HERO ================= */}
      <section ref={heroRef} className="relative min-h-[100vh] flex items-center overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 z-0 scale-110">
          <img src={SCENES.kingdomBg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-cream" />
        </motion.div>

        <Decorations dense />
        <motion.div style={{ x: cloudX }} className="absolute inset-0 pointer-events-none">
          <Cloud className="absolute left-[8%] top-[18%] text-white animate-floatySlow" size={70} fill="white" strokeWidth={1.5} />
          <Cloud className="absolute right-[12%] top-[24%] text-white animate-floaty" size={54} fill="white" strokeWidth={1.5} />
        </motion.div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-8 items-center w-full pt-24 pb-28">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="order-2 lg:order-1 relative rounded-[2.5rem] bg-white/55 backdrop-blur-md p-6 md:p-8 lg:p-10 shadow-[0_12px_44px_rgba(74,59,82,0.14)]">
            <motion.span variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur text-purpleK px-5 py-2 font-display font-semibold shadow-[0_5px_0_rgba(155,81,224,0.15)]">
              <Sparkles size={18} strokeWidth={2.5} /> Welcome to the Kingdom!
            </motion.span>
            <motion.h1 variants={fadeUp} custom={1}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.02] text-ink title-shadow">
              Hi Friends! Welcome to <span className="text-purpleK">Sir Gingerman's</span> <span className="text-orangeK">Kingdom!</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-5 text-xl md:text-2xl font-display font-semibold text-tealK">
              Let's Read, Learn, and Grow Together!
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-8 flex flex-wrap gap-4">
              <Btn as="link" to="/books" variant="purple" data-testid="hero-books-btn">
                <Wand2 size={20} /> Explore the Kingdom
              </Btn>
              <Btn as="link" to="/feelings" variant="white" data-testid="hero-feelings-btn">
                <Heart size={20} /> Heart Garden
              </Btn>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: charY }} initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }} className="relative order-1 lg:order-2 flex justify-center">
            <div className="absolute inset-0 -m-6 rounded-full bg-white/40 blur-3xl" />
            <div className="relative" data-testid="hero-gingerman-wrap"
              onMouseEnter={() => { setHovered(true); setGreet((g) => g + 1); }}
              onMouseLeave={() => setHovered(false)}
              onClick={() => setGreet((g) => g + 1)}>
              <motion.div aria-hidden className="absolute inset-0 -m-8 rounded-full bg-goldK/30 blur-3xl"
                animate={{ opacity: hovered ? 0.85 : 0.3, scale: hovered ? 1.06 : 1 }} transition={{ duration: 0.5, ease: "easeOut" }} />
              <AnimatedCharacter
                src={HERO_BOOKS}
                sprites={{ neutral: HERO_BOOKS, sad: HERO_BOOKS, joy: HERO_BOOKS }}
                blinkSrc={HERO_BOOKS_BLINK} followPointer
                emotion={hovered ? "joy" : "happy"}
                action="greet" actionKey={`greet-${greet}`}
                burst={greet > 0 ? "heart" : undefined} burstKey={`greet-${greet}`}
                alt="Sir Gingerman waving hello"
                className="relative w-[min(74vw,400px)] aspect-[856/1610] drop-shadow-[0_28px_45px_rgba(74,59,82,0.35)] cursor-pointer"
                testid="hero-gingerman" />
              <div className="absolute -bottom-3 -left-3 rounded-2xl bg-white px-4 py-2.5 shadow-xl flex items-center gap-2 animate-floaty">
                <Star className="text-goldK" fill="#FFC839" size={20} /> <span className="font-display font-semibold text-ink text-sm">Brave & Kind</span>
              </div>
              <Butterfly className="absolute -top-4 -right-2 animate-flutter" color="#FFC839" size={40} />
            </div>
            <img src={kingArt("crown-brave")} alt="" aria-hidden loading="eager" fetchPriority="high"
              className="hidden md:block absolute -bottom-4 -left-28 w-[135px] object-contain drop-shadow-[0_18px_28px_rgba(74,59,82,0.3)] animate-floaty" />
          </motion.div>
        </div>

        <CurvedDivider color="#FFF9F0" />
      </section>

      {/* ================= THREE BIG DOORS ================= */}
      <section className="max-w-6xl mx-auto px-6 -mt-4 relative z-10">
        <div className="grid gap-5 md:grid-cols-3">
          {bigButtons.map(({ to, label, Icon, bg, note }, i) => (
            <motion.div key={to} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <Link to={to} data-testid={`big-btn-${i}`}
                className={`group flex flex-col items-center text-center gap-3 rounded-[2rem] p-8 border-[5px] border-white text-white shadow-[0_16px_36px_rgba(0,0,0,0.12)] hover:-translate-y-1.5 transition-transform duration-200 ${bg}`}>
                <span className="grid place-items-center h-16 w-16 rounded-2xl bg-white/25 group-hover:animate-wiggle">
                  <Icon size={34} strokeWidth={2.5} />
                </span>
                <span className="font-display font-bold text-2xl">{label}</span>
                <span className="opacity-90">{note}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <SkyFeature dest={sky} />

      {/* ================= THE HEART OF THE KINGDOM (core cast) ================= */}
      <section className="max-w-6xl mx-auto px-6 mt-16" data-testid="home-core-cast">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block rounded-full bg-purpleK/10 text-purpleK font-display font-bold text-sm px-4 py-1.5">👑 The Heart of the Kingdom</span>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-ink mt-3">Meet the friends who lead the way</h2>
          <p className="text-ink/60 mt-2">Sir Gingerman, Mama Ginger and the Girl with the Green Eye — the brave hearts at the center of every Kingdom story.</p>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          {FRIENDS.slice(0, 3).map((f, i) => (
            <motion.div key={f.name} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
              className="rounded-[2rem] bg-white p-6 border-4 border-white shadow-[0_16px_36px_rgba(0,0,0,0.08)] text-center hover:-translate-y-1 transition-transform">
              <div className="mx-auto h-44 w-40 flex items-end justify-center">
                <AnimatedCharacter src={f.art} sprites={{ neutral: f.art, sad: f.art, joy: f.art }}
                  emotion="idle" groundShadow alt={f.name} className="h-44 w-40" testid={`home-core-${i}`} />
              </div>
              <h3 className="font-display font-extrabold text-xl text-ink mt-3">{f.name}</h3>
              <p className="font-display font-semibold text-sm mt-0.5" style={{ color: f.color }}>{f.role}</p>
              <p className="text-ink/60 text-sm mt-2">{f.bio}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/friends" data-testid="home-meet-all-friends"
            className="inline-flex items-center gap-2 rounded-full bg-purpleK text-white font-display font-bold px-6 py-3 hover:-translate-y-0.5 transition-transform">
            Meet all the Kingdom friends <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* ================= KINGDOM WORD OF THE WEEK ================= */}
      <section className="max-w-6xl mx-auto px-6 mt-14">
        <WordOfWeekWidget />
      </section>

      {/* ================= KINGDOM SHOWCASE (featured community artwork) ================= */}
      <FeaturedShowcase />

      {/* ================= DESTINATION: STORY CASTLE ================= */}
      <DestBand
        eyebrow="📚 Story Castle" title="Enter the Story Castle" tint="#9B51E0"
        desc="A towering castle built from books! Climb its shelves to find brave tales, playful workbooks and a journal made just for little dreamers."
        to="/books" cta="Open the Books" Icon={BookOpen} img={SCENES.storyCastle} bg="#efe4fb" />

      {/* ================= DESTINATION: CREATIVE CORNER ================= */}
      <section className="relative bg-orangeK/10 pt-20 pb-28">
        <CurvedDivider color="#FFF9F0" flip />
        <div className="max-w-[1300px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="rounded-[2.5rem] bg-gradient-to-br from-orangeK to-pinkK p-10 text-white shadow-[0_25px_50px_rgba(255,140,59,0.3)] overflow-hidden relative">
              <Sparkles className="absolute right-6 top-6 animate-twinkle" size={40} />
              <Palette className="w-24 h-24 animate-wiggle" strokeWidth={1.8} />
              <div className="mt-6 flex flex-wrap gap-3">
                {["Kindness", "Draw", "Dance", "Breathe"].map((t) => (
                  <span key={t} className="rounded-full bg-white/25 px-4 py-2 font-display font-semibold text-sm">{t}</span>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                {[Award, Star, Heart].map((I, k) => (
                  <span key={k} className="grid place-items-center h-14 w-14 rounded-2xl bg-white text-orangeK animate-floaty" style={{ animationDelay: `${k * 0.15}s` }}><I size={26} /></span>
                ))}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <DestText eyebrow="🎨 Creative Corner" title="Play in the Creative Corner" tint="#FF8C3B"
              desc="A cheerful clubhouse of daily activities, sticker rewards and badges. Every craft, dance and challenge helps little ones learn and shine."
              to="/activities" cta="Start Creating" Icon={Palette} />
          </div>
        </div>
      </section>

      {/* ================= DESTINATION: HEART GARDEN ================= */}
      <DestBand
        eyebrow="❤️ Heart Garden" title="Wander the Heart Garden" tint="#F25F9E" reverse
        desc="Where feelings bloom like flowers! Children pick a smiling heart to share how they feel and receive a warm, encouraging cheer from Sir Gingerman."
        to="/feelings" cta="Check In Today" Icon={Heart} img={SCENES.heartGarden} bg="#fde7f0" />

      {/* ================= DESTINATION: ADVENTURE CALENDAR ================= */}
      <section className="relative bg-tealK/10 pt-20 pb-28">
        <CurvedDivider color="#FFF9F0" flip />
        <div className="max-w-[1300px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="rounded-[2.5rem] bg-white p-6 shadow-[0_25px_50px_rgba(38,192,180,0.25)] border-[5px] border-white">
              <div className="rounded-3xl bg-tealK text-white p-4 flex items-center justify-between">
                <span className="font-display font-bold text-xl">Kingdom Calendar</span>
                <CalendarDays size={26} />
              </div>
              <div className="grid grid-cols-7 gap-2 mt-4">
                {Array.from({ length: 28 }).map((_, d) => {
                  const marked = [11, 19, 24].includes(d);
                  return (
                    <div key={d} className={`aspect-square grid place-items-center rounded-xl font-display font-semibold text-sm ${marked ? "bg-goldK text-ink animate-floaty" : "bg-cream text-ink/50"}`}>
                      {marked ? <Star size={16} fill="#4A3B52" /> : d + 1}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <DestText eyebrow="📅 Adventure Calendar" title="Follow the Adventure Calendar" tint="#26C0B4"
              desc="Story hours, workshops and magical celebrations! Tap a starred day to open an illustrated flyer and join the fun."
              to="/events" cta="See What's On" Icon={CalendarDays} />
          </div>
        </div>
      </section>

      {/* ================= DESTINATION: GINGER HEARTS KINGDOM ================= */}
      <DestBand
        eyebrow="👑 Ginger Hearts Kingdom" title="Join the Ginger Hearts Kingdom" tint="#7A6FF0"
        desc="An exclusive club where members earn badges, crowns and certificates as they read, create and grow. Unlock treasures on your journey to Royal Crown!"
        to="/membership" cta="Become a Member" Icon={Crown} img={SCENES.treasureChest} bg="#eae7fb" contain />

      {/* ================= MORE DESTINATIONS ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <span className="inline-block mb-3 rounded-full bg-purpleK/10 text-purpleK px-4 py-1.5 font-display font-semibold text-sm uppercase tracking-wide">More to explore</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-ink">Every corner is an adventure</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {previews.map(({ to, label, Icon, color, desc }, i) => (
            <motion.div key={to} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i % 4}>
              <Link to={to} data-testid={`preview-${label.toLowerCase().split(" ")[0]}`}
                className="group block h-full rounded-[2rem] bg-white p-7 border-4 border-white shadow-[0_12px_30px_rgba(0,0,0,0.06)] hover:border-goldK hover:-translate-y-2 transition-[transform,border-color] duration-200">
                <span className="grid place-items-center h-14 w-14 rounded-2xl mb-4 text-white group-hover:animate-wiggle" style={{ background: color }}>
                  <Icon size={28} strokeWidth={2.5} />
                </span>
                <h3 className="font-display font-bold text-xl text-ink">{label}</h3>
                <p className="mt-1.5 text-ink/60">{desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 font-display font-semibold" style={{ color }}>
                  Visit <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= CLOSING BANNER ================= */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-purpleK text-white p-10 md:p-16 text-center">
          <Sparkles className="absolute left-8 top-8 text-goldK animate-twinkle" size={40} />
          <Star className="absolute right-10 bottom-10 text-goldK animate-wiggle" size={34} fill="#FFC839" />
          <Butterfly className="absolute right-[18%] top-8 animate-flutter" color="#FFC839" size={34} />
          <h2 className="text-3xl md:text-4xl font-bold">A magical place to read, learn & grow</h2>
          <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto">Every story and activity helps children feel welcomed, brave and inspired.</p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Btn as="link" to="/membership" variant="gold" data-testid="banner-membership-btn"><Crown size={20} /> Join Ginger Hearts</Btn>
            <Btn as="link" to="/contact" variant="white" data-testid="banner-contact-btn"><Mail size={20} /> Say Hello</Btn>
          </div>
        </div>
      </section>
    </div>
  );
}

function DestText({ eyebrow, title, desc, to, cta, Icon, tint }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
      <span className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-display font-semibold text-sm text-white" style={{ background: tint }}>{eyebrow}</span>
      <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-ink leading-tight">{title}</h2>
      <p className="mt-4 text-lg text-ink/70 leading-relaxed">{desc}</p>
      <Link to={to} data-testid={`dest-${to.replace("/", "")}`}
        className="mt-7 inline-flex items-center gap-2 rounded-full text-white font-display font-semibold text-lg px-7 py-3.5 hover:-translate-y-0.5 active:translate-y-1 transition-transform"
        style={{ background: tint, boxShadow: `0 7px 0 ${tint}99` }}>
        <Icon size={20} /> {cta} <ArrowRight size={18} />
      </Link>
    </motion.div>
  );
}

function DestBand({ eyebrow, title, desc, to, cta, Icon, img, tint, bg, reverse, contain }) {
  return (
    <section className="relative pt-20 pb-28" style={{ background: bg }}>
      <CurvedDivider color="#FFF9F0" flip />
      <div className="max-w-[1300px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }} className={`relative ${reverse ? "lg:order-2" : ""}`}>
          <div className="absolute inset-0 -m-4 rounded-[3rem] blur-2xl animate-floatySlow" style={{ background: `${tint}33` }} />
          <div className={`relative rounded-[2.5rem] overflow-hidden border-[6px] border-white shadow-[0_25px_50px_rgba(0,0,0,0.15)] ${contain ? "bg-white p-4" : ""}`}>
            <img src={img} alt={title} className={`w-full ${contain ? "h-[360px] object-contain" : "h-[420px] object-cover"}`} />
          </div>
          <span className="absolute -bottom-4 left-6 grid place-items-center h-16 w-16 rounded-2xl text-white shadow-xl animate-bob" style={{ background: tint }}><Icon size={30} strokeWidth={2.4} /></span>
        </motion.div>
        <div className={reverse ? "lg:order-1" : ""}>
          <DestText eyebrow={eyebrow} title={title} desc={desc} to={to} cta={cta} Icon={Icon} tint={tint} />
        </div>
      </div>
    </section>
  );
}
